import Vue from 'vue';
import types from '../mutation-types';
import authAPI from '../../api/auth';

import {
  setUser,
  clearCookiesOnLogout,
  deleteIndexedDBOnLogout,
} from '../utils/api';
import DashboardAudioNotificationHelper from 'dashboard/helper/AudioAlerts/DashboardAudioNotificationHelper';
import { isFeatureFlagEnabledForAccount } from 'dashboard/helper/featureFlagHelper';

const initialState = {
  currentUser: {
    id: null,
    account_id: null,
    accounts: [],
    email: null,
    name: null,
  },
  availableInstances: [],
  uiFlags: {
    isFetching: true,
  },
};

// getters
export const getters = {
  isLoggedIn($state) {
    return !!$state.currentUser.id;
  },

  getCurrentUserID($state) {
    return $state.currentUser.id;
  },

  getUISettings($state) {
    return $state.currentUser.ui_settings || {};
  },

  getAuthUIFlags($state) {
    return $state.uiFlags;
  },

  getCurrentUserAvailability($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.availability;
  },

  getCurrentUserAvailabilityReason($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.availability_reason;
  },

  getCurrentUserAutoOffline($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.auto_offline;
  },

  getCurrentAccountId(_, __, rootState) {
    if (rootState.route.params && rootState.route.params.accountId) {
      return Number(rootState.route.params.accountId);
    }
    return null;
  },

  getCurrentRole($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.role;
  },

  getCurrentCustomRoleId($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.custom_role_id;
  },

  getCurrentUser($state) {
    return $state.currentUser;
  },

  getMessageSignature($state) {
    const { message_signature: messageSignature } = $state.currentUser;

    return messageSignature || '';
  },

  getCurrentUserPubsubToken($state) {
    return $state.currentUser.pubsub_token;
  },

  getCurrentAccount($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount || {};
  },

  isNewFeatureFlagEnabled: ($state, $getters) => newFeatureFlag => {
    // Resolve the flag value for the CURRENT account. The account selection
    // stays here; only the (dotted-path aware) value extraction is shared with
    // the Improvement Center route guard via isFeatureFlagEnabledForAccount.
    return isFeatureFlagEnabledForAccount(
      $getters.getCurrentAccount,
      newFeatureFlag
    );
  },

  getUserAccounts($state) {
    const { accounts = [] } = $state.currentUser;
    return accounts;
  },

  getAvailableInstances($state) {
    return $state.availableInstances;
  },
};

// actions
export const actions = {
  async validityCheck(context) {
    try {
      const response = await authAPI.validityCheck();
      const currentUser = response.data.payload.data;
      setUser(currentUser);
      context.commit(types.SET_CURRENT_USER, currentUser);
    } catch (error) {
      if (error?.response?.status === 401) {
        clearCookiesOnLogout();
      }
    }
  },
  async setUser({ commit, dispatch }) {
    if (authAPI.hasAuthCookie()) {
      await dispatch('validityCheck');
    } else {
      commit(types.CLEAR_USER);
    }
    commit(types.SET_CURRENT_USER_UI_FLAGS, { isFetching: false });
  },
  logout({ commit }) {
    commit(types.CLEAR_USER);
  },

  // Refreshes the Cognito id_token surfaced via currentUser so Claudia
  // microfrontends keep a valid Bearer past Cognito's 24h id_token TTL.
  // The backend transparently swaps the id_token via the stored refresh_token
  // when it is within 15 minutes of expiry — this action just pulls the
  // result and pushes it into the store so the microfrontend props re-render.
  async refreshCognitoAccessToken({ commit }) {
    try {
      const { data } = await authAPI.cognitoIdToken();
      if (data?.id_token) {
        commit(types.UPDATE_COGNITO_ACCESS_TOKEN, data.id_token);
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        // Mirror the normal logout cleanup order: wipe IndexedDB first
        // (clearCookiesOnLogout navigates away, so anything after it
        // never runs). deleteIndexedDBOnLogout is async — without the
        // await, indexedDB.deleteDatabase() onsuccess fires after the
        // navigation has already torn down the page, leaving another
        // user's cached data on the device.
        await deleteIndexedDBOnLogout();
        clearCookiesOnLogout();
      }
    }
  },

  updateProfile: async ({ commit }, params) => {
    // eslint-disable-next-line no-useless-catch
    try {
      const response = await authAPI.profileUpdate(params);
      commit(types.SET_CURRENT_USER, response.data);
    } catch (error) {
      throw error;
    }
  },

  deleteAvatar: async () => {
    try {
      await authAPI.deleteAvatar();
    } catch (error) {
      // Ignore error
    }
  },

  updateUISettings: async ({ commit }, params) => {
    try {
      commit(types.SET_CURRENT_USER_UI_SETTINGS, params);
      const response = await authAPI.updateUISettings(params);
      commit(types.SET_CURRENT_USER, response.data);

      if (
        DashboardAudioNotificationHelper.needsToUpdateAudioState(response.data)
      )
        DashboardAudioNotificationHelper.setInstanceValues(response.data);
    } catch (error) {
      // Ignore error
    }
  },

  updateAvailability: async ({ commit, dispatch }, params) => {
    try {
      const response = await authAPI.updateAvailability(params);
      const userData = response.data;
      const { id } = userData;
      commit(types.SET_CURRENT_USER, response.data);
      dispatch('agents/updateSingleAgentPresence', {
        id,
        availabilityStatus: params.availability,
      });
    } catch (error) {
      // Ignore error
    }
  },

  updateAutoOffline: async ({ commit }, { accountId, autoOffline }) => {
    try {
      const response = await authAPI.updateAutoOffline(accountId, autoOffline);
      commit(types.SET_CURRENT_USER, response.data);
    } catch (error) {
      // Ignore error
    }
  },

  setCurrentUserAvailability({ commit, state: $state }, data) {
    if (data[$state.currentUser.id]) {
      commit(types.SET_CURRENT_USER_AVAILABILITY, data[$state.currentUser.id]);
    }
  },

  setCurrentUserAccountFields({ commit }, { accountId, fields }) {
    commit(types.SET_CURRENT_USER_ACCOUNT_FIELDS, { accountId, fields });
  },

  setActiveAccount: async (_, { accountId }) => {
    try {
      await authAPI.setActiveAccount({ accountId });
    } catch (error) {
      // Ignore error
    }
  },

  resendConfirmation: async () => {
    try {
      await authAPI.resendConfirmation();
    } catch (error) {
      // Ignore error
    }
  },

  regenerateToken: async ({ commit }) => {
    const response = await authAPI.regenerateToken();
    commit(types.SET_CURRENT_USER, response.data);
  },

  fetchAvailableInstances: async ({ commit }) => {
    try {
      const response = await authAPI.getProfileInstances();
      commit(types.SET_AVAILABLE_INSTANCES, response?.data || []);
    } catch (error) {
      // Ignore error — instances section simply won't show
    }
  },
};

// mutations
export const mutations = {
  [types.SET_CURRENT_USER_AVAILABILITY](_state, availability) {
    const accounts = _state.currentUser.accounts.map(account => {
      if (account.id === _state.currentUser.account_id) {
        return { ...account, availability, availability_status: availability };
      }
      return account;
    });
    Vue.set(_state, 'currentUser', {
      ..._state.currentUser,
      accounts,
    });
  },
  [types.SET_CURRENT_USER_ACCOUNT_FIELDS](_state, { accountId, fields }) {
    const accounts = _state.currentUser.accounts.map(account => {
      if (account.id === accountId) {
        return { ...account, ...fields };
      }
      return account;
    });
    Vue.set(_state, 'currentUser', {
      ..._state.currentUser,
      accounts,
    });
  },
  [types.CLEAR_USER](_state) {
    _state.currentUser = initialState.currentUser;
    _state.availableInstances = [];
  },
  [types.SET_CURRENT_USER](_state, currentUser) {
    Vue.set(_state, 'currentUser', currentUser);
  },
  [types.SET_CURRENT_USER_UI_SETTINGS](_state, { uiSettings }) {
    Vue.set(_state, 'currentUser', {
      ..._state.currentUser,
      ui_settings: {
        ..._state.currentUser.ui_settings,
        ...uiSettings,
      },
    });
  },

  [types.SET_CURRENT_USER_UI_FLAGS](_state, { isFetching }) {
    Vue.set(_state, 'uiFlags', { isFetching });
  },

  [types.SET_AVAILABLE_INSTANCES](_state, instances) {
    Vue.set(_state, 'availableInstances', instances);
  },

  [types.UPDATE_COGNITO_ACCESS_TOKEN](_state, idToken) {
    // Update only the cognito_access_token field; do not replace the whole
    // currentUser object. Spreading into a new object creates a fresh ref
    // every refresh (every 10 minutes), so every watcher/computed that
    // depends on currentUser would re-run unnecessarily. Per-key Vue.set
    // makes only consumers of cognito_access_token re-evaluate.
    Vue.set(_state.currentUser, 'cognito_access_token', idToken);
  },
};

export default {
  state: initialState,
  getters,
  actions,
  mutations,
};
